import { createAsyncThunk } from "@reduxjs/toolkit";
import { postLoginService } from "../../Services";
import { alert } from "../../utils/commonFunctions/alert";
import {
  getStoredUser,
  syncStoredMerchantIdentity,
} from "../../utils/auth/storedUser";

const getErrorMessage = (error, fallbackMessage) =>
  error?.response?.data?.message ||
  error?.response?.data?.error?.message ||
  error?.message ||
  fallbackMessage;

const isMissingMerchantIdError = (value) =>
  typeof value === "string" && value.toLowerCase().includes("merchant id not found");

const toNumericId = (value) => {
  if (value == null || value === "") return null;
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed <= 0) return null;
  return parsed;
};

const normalizeText = (value) => {
  if (typeof value !== "string") return "";
  return value.trim();
};

const enrichProposalTerms = (payload, termCatalog) => {
  const proposals = Array.isArray(payload?.proposals) ? payload.proposals : [];
  if (!proposals.length || !Array.isArray(termCatalog) || !termCatalog.length) {
    return payload;
  }

  const termMap = new Map(
    termCatalog
      .map((term) => {
        const id = toNumericId(term?.id);
        const text = normalizeText(term?.term || term?.name || term?.title);
        if (id === null || !text) return null;
        return [id, text];
      })
      .filter(Boolean)
  );

  if (!termMap.size) return payload;

  const enrichedProposals = proposals.map((proposal) => {
    const dealTerms = proposal?.deal?.dealTerms;
    if (!Array.isArray(dealTerms) || !dealTerms.length) return proposal;

    const normalizedTerms = dealTerms.map((term) => {
      let parsedTerm = term;

      if (typeof parsedTerm === "string") {
        const trimmed = parsedTerm.trim();
        if (trimmed.startsWith("{")) {
          try {
            parsedTerm = JSON.parse(trimmed);
          } catch {
            parsedTerm = term;
          }
        }
      }

      if (parsedTerm && typeof parsedTerm === "object") {
        const id = toNumericId(parsedTerm.id);
        const text = normalizeText(parsedTerm.term || parsedTerm.name || parsedTerm.title || parsedTerm.label);
        if (id !== null && !text && termMap.has(id)) {
          return { ...parsedTerm, term: termMap.get(id) };
        }
        return parsedTerm;
      }

      const scalarId = toNumericId(parsedTerm);
      if (scalarId !== null && termMap.has(scalarId)) {
        return { id: scalarId, term: termMap.get(scalarId) };
      }

      return parsedTerm;
    });

    return {
      ...proposal,
      deal: {
        ...proposal.deal,
        dealTerms: normalizedTerms,
      },
    };
  });

  return {
    ...payload,
    proposals: enrichedProposals,
  };
};

const fetchCurrentMerchantProfile = async () => {
  const response = await postLoginService.get(`/api/merchants`);

  if (!response?.success) {
    throw new Error(response?.message || "Failed to fetch merchant data");
  }

  const merchantData = response?.data || response;
  syncStoredMerchantIdentity(merchantData);
  return merchantData;
};

const requestWithMerchantRecovery = async (requestFn, fallbackMessage) => {
  try {
    const response = await requestFn();

    if (response?.success === false && isMissingMerchantIdError(response?.message)) {
      await fetchCurrentMerchantProfile();
      return requestFn();
    }

    return response;
  } catch (error) {
    if (isMissingMerchantIdError(getErrorMessage(error, ""))) {
      await fetchCurrentMerchantProfile();
      return requestFn();
    }

    throw error;
  }
};

// Get all merchants (for influencers to browse)
export const getAllMerchants = createAsyncThunk(
  "merchant/getAllMerchants",
  async (_, { rejectWithValue }) => {
    try {
      const url = `/api/merchants/all`;
      const response = await postLoginService.get(url);
      // console.log("ALL MERCHANTS RESPONSE---->", response);

      if (response?.success) {
        return response.data;
      } else {
        alert.error(response?.message || "Failed to fetch merchants");
        return rejectWithValue(
          response?.message || "Failed to fetch merchants"
        );
      }
    } catch (error) {
      const errorMessage = error?.message || "Failed to fetch merchants";
      alert.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// Get deals for a specific merchant
export const getMerchantDeals = createAsyncThunk(
  "merchant/getMerchantDeals",
  async (merchantId, { rejectWithValue }) => {
    try {
      const url = `/api/deals/merchant/${merchantId}`;
      const response = await postLoginService.get(url);
      // console.log("MERCHANT DEALS RESPONSE---->777", response);

      if (response?.success) {
        return {
          merchantId,
          deals: response.data || [],
        };
      } else {
        alert.error(response?.message || "Failed to fetch merchant deals");
        return rejectWithValue(
          response?.message || "Failed to fetch merchant deals"
        );
      }
    } catch (error) {
      const errorMessage = error?.message || "Failed to fetch merchant deals";
      alert.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// Get merchant profile data
export const getMerchantProfile = createAsyncThunk(
  "merchant/getMerchantProfile",
  async (_, { rejectWithValue }) => {
    try {
      const url = `/api/merchants`;
      const response = await postLoginService.get(url);
      // console.log("MERCHANT PROFILE RESPONSE---->", response);

      if (response?.success) {
        syncStoredMerchantIdentity(response?.data || response);
        return response;
      } else {
        alert.error(response?.message || "Failed to fetch merchant data");
        return rejectWithValue(
          response?.message || "Failed to fetch merchant data"
        );
      }
    } catch (error) {
      const statusCode = error?.response?.status || error?.status;
      const isAuthTransitionError = statusCode === 401 || statusCode === 403;
      const errorMessage = error?.message || "Failed to fetch merchant data";
      if (!isAuthTransitionError) {
        alert.error(errorMessage);
      }
      return rejectWithValue(errorMessage);
    }
  }
);

// Update merchant profile
export const updateMerchantProfile = createAsyncThunk(
  "merchant/updateMerchantProfile",
  async (data, { rejectWithValue }) => {
    try {
      const url = `/api/merchants/me`;
      const response = await postLoginService.put(url, data);
      // console.log("UPDATE MERCHANT RESPONSE---->", response);

      if (response?.success) {
        syncStoredMerchantIdentity(response?.data || response);
        // alert.success("Profile updated successfully!");
        return response;
      } else {
        alert.error(response?.message || "Failed to update profile");
        return rejectWithValue(response?.message || "Failed to update profile");
      }
    } catch (error) {
      const errorMessage = error?.message || "Failed to update profile";
      alert.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// Upload merchant profile image
// MerchantThunk.js
export const uploadProfileImage = createAsyncThunk(
  "merchant/uploadProfileImage",
  async (file, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("image", file);

      const url = `/api/merchants/profile/image`;
      const response = await postLoginService.post(url, formData);

      if (response?.success) {
        // alert.success(response?.message || "Profile image uploaded successfully!");
        return response;
      } else {
        alert.error(response?.message || "Failed to upload profile image");
        return rejectWithValue(response?.message);
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error, "Failed to fetch merchant config");
      alert.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);
// Get merchant config
export const getMerchantConfig = createAsyncThunk(
  "merchant/getMerchantConfig",
  async (_, { rejectWithValue }) => {
    try {
      const url = `/api/merchants/me/config`;
      const response = await postLoginService.get(url);
      // console.log("MERCHANT CONFIG RESPONSE---->", response);

      if (response?.success) {
        return response;
      } else {
        alert.error(response?.message || "Failed to fetch merchant config");
        return rejectWithValue(
          response?.message || "Failed to fetch merchant config"
        );
      }
    } catch (error) {
      const errorMessage = error?.message || "Failed to fetch merchant config";
      alert.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// Update merchant config
export const updateMerchantConfig = createAsyncThunk(
  "merchant/updateMerchantConfig",
  async (data, { rejectWithValue }) => {
    try {
      const response = await requestWithMerchantRecovery(
        () => postLoginService.put(`/api/merchants/me/config`, data),
        "Failed to update settings"
      );
      // console.log("UPDATE MERCHANT CONFIG RESPONSE---->", response);

      if (response?.success) {
        // alert.success("Settings updated successfully!");
        return response;
      } else {
        alert.error(response?.message || "Failed to update settings");
        return rejectWithValue(response?.message || "Failed to update settings");
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error, "Failed to update settings");
      alert.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// Change merchant password
export const changeMerchantPassword = createAsyncThunk(
  "merchant/changeMerchantPassword",
  async (data, { rejectWithValue }) => {
    try {
      const url = `/api/auth/change-password`;
      const response = await postLoginService.post(url, data);
      // console.log("CHANGE PASSWORD RESPONSE---->", response);

      if (response?.success) {
        // alert.success(response?.message || "Password changed successfully!");
        return response;
      } else {
        const errorMessage = response?.error?.message || response?.message || "Failed to change password";
        alert.error(errorMessage);
        return rejectWithValue(errorMessage);
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to change password";
      alert.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);


// Get merchant's deal proposals (from influencers)
export const getMerchantDealProposals = createAsyncThunk(
  "merchant/getMerchantDealProposals",
  async (params = {}, { rejectWithValue }) => {
    try {
      const { status, page = 1 } = params;

      let url = `/api/merchants/deal-proposals?page=${page}`;

      if (status) {
        url += `&status=${status}`;
      }

      const response = await postLoginService.get(url);
      // console.log("GET MERCHANT DEAL PROPOSALS RESPONSE---->", response);

      if (response?.success) {
        let enrichedPayload = response.data;

        try {
          const termsResponse = await postLoginService.get(`/api/deal-terms/merchant`);
          if (termsResponse?.success) {
            const termCatalog = termsResponse?.data?.data || termsResponse?.data || [];
            enrichedPayload = enrichProposalTerms(response.data, termCatalog);
          }
        } catch (_error) {
          // Keep proposal list usable even if term-catalog enrichment fails.
        }

        return enrichedPayload;
      } else {
        alert.error(response?.message || "Failed to fetch deal proposals");
        return rejectWithValue(
          response?.message || "Failed to fetch deal proposals"
        );
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch deal proposals";
      alert.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// Approve deal proposal
export const approveDealProposal = createAsyncThunk(
  "merchant/approveDealProposal",
  async (proposalId, { rejectWithValue }) => {
    try {
      const url = `/api/merchants/deal-proposals/${proposalId}/approve`;
      const response = await postLoginService.put(url);
      // console.log("APPROVE DEAL PROPOSAL RESPONSE---->", response);

      if (response?.success) {
        // alert.success(response?.message || "Deal proposal approved successfully!");
        return response.data;
      } else {
        alert.error(response?.message || "Failed to approve proposal");
        return rejectWithValue(response?.message || "Failed to approve proposal");
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to approve proposal";
      alert.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// Reject deal proposal
export const rejectDealProposal = createAsyncThunk(
  "merchant/rejectDealProposal",
  async ({ proposalId, rejectionReason }, { rejectWithValue }) => {
    try {
      const url = `/api/merchants/deal-proposals/${proposalId}/reject`;
      const response = await postLoginService.put(url, { rejectionReason });
      // console.log("REJECT DEAL PROPOSAL RESPONSE---->", response);

      if (response?.success) {
        // alert.success(response?.message || "Deal proposal rejected");
        return response.data;
      } else {
        alert.error(response?.message || "Failed to reject proposal");
        return rejectWithValue(response?.message || "Failed to reject proposal");
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to reject proposal";
      alert.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// Save merchant bank details for payouts
export const saveBankDetails = createAsyncThunk(
  "merchant/saveBankDetails",
  async (data, { rejectWithValue }) => {
    try {
      const user = getStoredUser();
      // console.log("356--->STORED USER IN SAVE BANK DETAILS---->", user);
      if (!user) {
        alert.error("User not found. Please login again.");
        return rejectWithValue("User not found");
      }

      let merchantId = user?.profile?.merchantId;
      // console.log("363--->MERCHANT ID FROM STORED USER---->", merchantId);
      if (!merchantId) {
        const merchantData = await fetchCurrentMerchantProfile();
        merchantId = merchantData?.id || merchantData?._id || merchantData?.merchantId;
      }

      if (!merchantId) {
        alert.error("Merchant ID not found. Please contact support.");
        return rejectWithValue("Merchant ID not found");
      }

      const url = `/api/payouts/merchant/${merchantId}/bank-details`;
      // console.log("375-->URL FOR SAVING BANK DETAILS---->", url);
      const response = await postLoginService.post(url, data);
      // console.log("SAVE BANK DETAILS RESPONSE---->", response);

      if (response?.success) {
        // alert.success(response?.message || "Bank details saved successfully!");
        return response.data;
      } else {
        alert.error(response?.message || "Failed to save bank details");
        return rejectWithValue(response?.message || "Failed to save bank details");
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to save bank details";
      alert.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);


//general info update
export const updateMerchantGeneralConfig = createAsyncThunk(
  "merchant/updateMerchantGeneralConfig",
  async (data, { rejectWithValue }) => {
    try {
      const response = await requestWithMerchantRecovery(
        () => postLoginService.put(
          "/api/merchants/me/config",
          data
        ),
        "Failed to update general info"
      );

      if (response?.success) {
        // alert.success("General information updated");
        return response.data;
      }
      return rejectWithValue(response?.message);
    } catch (err) {
      const errorMessage = getErrorMessage(err, "Failed to update general info");
      alert.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);


//Delivery Settings Update
export const updateMerchantDeliveryConfig = createAsyncThunk(
  "merchant/updateMerchantDeliveryConfig",
  async (data, { rejectWithValue }) => {
    try {
      const response = await requestWithMerchantRecovery(
        () => postLoginService.put(
          "/api/merchants/me/config",
          data
        ),
        "Failed to update delivery settings"
      );

      if (response?.success) {
        // alert.success("Delivery settings updated");
        return response.data;
      }
      return rejectWithValue(response?.message);
    } catch (err) {
      const errorMessage = getErrorMessage(err, "Failed to update delivery settings");
      alert.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);


// Business Hours Update
export const updateMerchantHoursConfig = createAsyncThunk(
  "merchant/updateMerchantHoursConfig",
  async (data, { rejectWithValue }) => {
    try {
      const response = await requestWithMerchantRecovery(
        () => postLoginService.put(
          "/api/merchants/me/config",
          data
        ),
        "Failed to update business hours"
      );

      if (response?.success) {
        // alert.success("Business hours updated");
        return response.data;
      }
      return rejectWithValue(response?.message);
    } catch (err) {
      const errorMessage = getErrorMessage(err, "Failed to update business hours");
      alert.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

